/* =========================================================
   Curlstique – Unified JS
   - Sticky ATC behavior (no submit handling here)
   - Cart Drawer (instant open + reliable images)
   - Universal Add-to-Cart (captures all /cart/add submits)
   ========================================================= */

/* ---------------------------
   Sticky ATC: visibility + variant sync
   (Submission is handled by the universal handler below)
--------------------------- */
(function(){
  // Only run on product pages
  if (!document.body.classList.contains('template-product')) return;

  const bar = document.getElementById('cl-sticky-atc');
  if (!bar) return;

  // Reveal logic: show bar when main area scrolls out of view
  const sentinel = document.querySelector('#MainContent, main, .product, .product__title') || document.body;
  const io = new IntersectionObserver(entries => {
    entries.forEach(e => {
      // When the top content is out of view, show the bar
      bar.hidden = e.isIntersecting;
    });
  }, { rootMargin: '-120px 0px 0px 0px', threshold: 0 });
  io.observe(sentinel);

  // Keep variant id in sync with the main product form (if present)
  const pageForm = document.querySelector('form[action*="/cart/add"]');
  const idInput = bar.querySelector('input[name="id"]');
  if (pageForm && idInput) {
    const variantSelect = pageForm.querySelector('select[name="id"]');
    if (variantSelect) {
      // Initialize once
      idInput.value = variantSelect.value;
      // Update on change
      variantSelect.addEventListener('change', () => {
        idInput.value = variantSelect.value;
      });
    }
  }
})();

/* ---------------------------
   Cart Drawer logic
   (opens instantly on add, then renders current cart;
    robust image URLs; skeleton while loading)
--------------------------- */
(function(){
  const drawer = document.getElementById('cl-cart-drawer');
  const overlay = document.getElementById('cl-cart-overlay');
  const itemsEl = document.getElementById('cl-cart-items');
  const subEl = document.getElementById('cl-cart-subtotal');
  if (!drawer || !overlay || !itemsEl || !subEl) return;

  // Helper: add ?width=NN or &width=NN safely
  const withWidth = (url, w) => {
    if (!url) return '';
    try {
      const sep = url.includes('?') ? '&' : '?';
      return `${url}${sep}width=${w}`;
    } catch { return url; }
  };

  const fmt = (cents) => {
    const cur = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'KWD';
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: cur }).format((cents || 0)/100);
  };

  function openDrawer(){
    overlay.hidden = false;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden','false');
  }
  function closeDrawer(){
    overlay.hidden = true;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden','true');
  }

  // Open instantly on add; render right after
  const openAndRender = () => { openDrawer(); renderCart(); };

  document.addEventListener('cl:open-cart-drawer', openAndRender);
  document.addEventListener('cl:added-to-cart', openAndRender);

  // Close actions
  overlay.addEventListener('click', closeDrawer);
  const closeBtn = drawer.querySelector('.cl-cart-close');
  if (closeBtn) closeBtn.addEventListener('click', closeDrawer);
  document.addEventListener('keydown', (e)=>{ if(e.key==='Escape') closeDrawer(); });

  async function renderCart(){
    // Skeleton while loading
    itemsEl.innerHTML = `
      <div class="cl-skel"></div>
      <div class="cl-skel"></div>
      <div class="cl-skel"></div>
    `;
    try {
      const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' }});
      const cart = await res.json();
      subEl.textContent = fmt(cart.total_price);

      if (!cart.items.length) {
        itemsEl.innerHTML = '<div class="cl-cart-empty">Your cart is empty</div>';
        return;
      }

      itemsEl.innerHTML = cart.items.map(item => {
        const img = withWidth(item.image, 150);
        const variant = item.variant_title ? `<div class="cl-ci-variant">${item.variant_title}</div>` : '';
        return `
          <div class="cl-ci" data-key="${item.key}">
            ${img ? `<img src="${img}" alt="" loading="eager" decoding="async">` : `<div style="width:64px;height:64px;border-radius:8px;border:1px solid var(--cl-border);background:var(--cl-bg-soft);"></div>`}
            <div>
              <div class="cl-ci-title">${item.product_title}</div>
              ${variant}
              <div class="cl-ci-qty">
                <button type="button" data-act="dec" aria-label="Decrease">−</button>
                <span>${item.quantity}</span>
                <button type="button" data-act="inc" aria-label="Increase">＋</button>
                <button type="button" data-act="rm" aria-label="Remove" style="margin-left:8px;">Remove</button>
              </div>
            </div>
            <div class="cl-ci-price">${fmt(item.line_price)}</div>
          </div>
        `;
      }).join('');

      // Bind qty buttons
      itemsEl.querySelectorAll('.cl-ci button').forEach(btn=>{
        btn.addEventListener('click', async ()=>{
          const row = btn.closest('.cl-ci');
          const key = row.getAttribute('data-key');
          const act = btn.getAttribute('data-act');
          const qtyEl = row.querySelector('.cl-ci-qty span');
          let qty = parseInt(qtyEl.textContent, 10);

          if (act === 'inc') qty++;
          if (act === 'dec') qty = Math.max(0, qty - 1);
          if (act === 'rm') qty = 0;

          const resp = await fetch('/cart/change.js', {
            method:'POST',
            headers:{ 'Content-Type':'application/json' },
            body: JSON.stringify({ id: key, quantity: qty })
          });
          if (resp.ok) renderCart();
        });
      });
    } catch {
      itemsEl.innerHTML = '<div class="cl-cart-empty">Couldn’t load cart. Try again.</div>';
    }
  }
})();

/* ---------------------------
   Universal Add-to-Cart
   - Captures ANY form posting to /cart/add
   - Prevents redirect, adds via AJAX
   - Announces "added" so the drawer opens instantly
--------------------------- */
(function(){
  // Intercept every /cart/add form submit (capture phase)
  document.addEventListener('submit', async (e) => {
    const form = e.target;
    if (!form.matches('form[action*="/cart/add"]')) return;

    e.preventDefault();
    e.stopPropagation();

    const btn = form.querySelector('[type="submit"], button[name="add"]');
    const original = btn ? btn.textContent : null;
    if (btn) { btn.disabled = true; btn.textContent = 'Adding…'; }

    try {
      const fd = new FormData(form);
      const id = fd.get('id');
      const quantity = parseInt(fd.get('quantity') || '1', 10) || 1;
      if (!id) throw new Error('Missing variant id');

      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, quantity })
      });

      // Open drawer instantly
      document.dispatchEvent(new CustomEvent('cl:added-to-cart'));

      if (btn) {
        btn.textContent = 'Added ✓';
        setTimeout(() => { btn.textContent = original; btn.disabled = false; }, 900);
      }
    } catch (err) {
      console.error(err);
      if (btn) {
        btn.textContent = 'Error';
        setTimeout(() => { btn.textContent = original; btn.disabled = false; }, 1200);
      }
    }
  }, true);

  // Some themes bind click handlers to the button instead of the form.
  // This forces a form submit we can intercept above (still no redirect).
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('button[name="add"], .product-form__submit, [data-add-to-cart]');
    if (!btn) return;
    const form = btn.closest('form[action*="/cart/add"]');
    if (!form) return;
    e.preventDefault();
    e.stopPropagation();
    if (form.requestSubmit) form.requestSubmit(); else form.submit();
  }, true);
})();
// === Predictive Search: open on focus, show results reliably ===
(function(){
  // Find the search input (Pipeline uses data-predictive-search-input)
  const input = document.querySelector('input[type="search"][name="q"][aria-controls], input[data-predictive-search-input]');
  const popdown = document.getElementById('cl-search-root');
  if (!input || !popdown) return;

  // The theme’s results container id from aria-controls (e.g., "predictive-search-results")
  const nativeResultsId = input.getAttribute('aria-controls');
  const nativeResults = nativeResultsId ? document.getElementById(nativeResultsId) : null;

  // Open/close helpers
  const open = () => { popdown.hidden = false; };
  const close = () => { popdown.hidden = true; };

  // Close on outside click / ESC
  document.addEventListener('click', (e) => {
    if (popdown.contains(e.target) || input.contains(e.target)) return;
    close();
  });
  document.addEventListener('keydown', (e)=>{ if (e.key === 'Escape') close(); });

  // Force the theme’s predictive script to fetch as user types (if it exists)
  let t;
  input.addEventListener('input', () => {
    const val = (input.value || '').trim();
    clearTimeout(t);
    open();
    // Empty state
    if (val.length < 2) {
      document.getElementById('cl-search-body').innerHTML = `
        <div class="cl-search-empty">Start typing to see results…</div>
        <div class="cl-search-suggest">
          <a href="/search?q=shampoo">Shampoo</a>
          <a href="/search?q=conditioner">Conditioner</a>
          <a href="/search?q=leave+in">Leave-in</a>
          <a href="/search?q=gel">Gel</a>
          <a href="/search?q=curly+bundle">Curly bundle</a>
        </div>`;
      return;
    }
    // Debounce slightly, then trigger built-in predictive (if present)
    t = setTimeout(() => {
      // Many themes listen for the native 'input' event to re-fetch
      const evt = new Event('input', { bubbles: true });
      input.dispatchEvent(evt);
    }, 120);
  });

  // On focus, open popdown immediately
  input.addEventListener('focus', () => {
    open();
    if (!input.value) {
      document.getElementById('cl-search-body').innerHTML = `
        <div class="cl-search-empty">Try: “shampoo”, “gel”, “leave-in”…</div>
        <div class="cl-search-suggest">
          <a href="/search?q=shampoo">Shampoo</a>
          <a href="/search?q=gel">Gel</a>
          <a href="/search?q=mask">Mask</a>
          <a href="/search?q=brush">Brush</a>
        </div>`;
    }
  });

  // If the theme renders a native list, mirror it into our popdown for consistent styling
  const renderFromNative = () => {
    if (!nativeResults) return;
    // Grab products if the theme outputs a list; try common selectors
    const items = nativeResults.querySelectorAll('a[href*="/products/"]');
    if (!items.length) return;
    const rows = [];
    items.forEach(a => {
      const title = a.querySelector('[data-predictive-search-title], .predictive-search__item-heading, .js-predictive-link-title')?.textContent?.trim() || a.textContent.trim();
      const price = a.querySelector('[data-predictive-search-price], .price, .money')?.textContent?.trim() || '';
      const img = a.querySelector('img')?.getAttribute('src') || a.querySelector('img')?.getAttribute('data-src') || '';
      const imgUrl = img ? (img.includes('://') ? img : ('https:' + img)) : '';
      rows.push(`
        <a class="cl-search-item" href="${a.getAttribute('href')}">
          ${imgUrl ? `<img class="cl-search-img" src="${imgUrl}${imgUrl.includes('?') ? '&' : '?'}width=120" alt="">` : `<div class="cl-search-img" style="background:var(--cl-bg-soft);"></div>`}
          <div>
            <div class="cl-search-title">${title}</div>
          </div>
          <div class="cl-search-price">${price}</div>
        </a>
      `);
    });
    document.getElementById('cl-search-body').innerHTML = `
      <ul class="cl-search-list"><li>${rows.join('</li><li>')}</li></ul>
    `;
  };

  // Observe the native container; when it updates, mirror into ours
  if (nativeResults) {
    const obs = new MutationObserver(renderFromNative);
    obs.observe(nativeResults, { childList: true, subtree: true });
  }
})();
// Prevent empty search form from navigating away; focus input + open predictive
(function(){
  const wrapper = document.querySelector('[data-search-popdown-wrap]');
  if (!wrapper) return;
  const form = wrapper.querySelector('form[action*="' + (window.Shopify && Shopify.routes ? Shopify.routes.search_url : '/search') + '"]') || wrapper.querySelector('form[role="search"]');
  const btn  = wrapper.querySelector('.search__predictive__button[type="submit"]');
  const input = wrapper.querySelector('input[type="search"][name="q"]');

  if (!form || !btn || !input) return;

  btn.addEventListener('click', (e) => {
    const q = (input.value || '').trim();
    if (q.length < 2) {
      e.preventDefault();
      e.stopPropagation();
      input.focus();
      // Let the theme’s predictive search open/fetch
      const evt = new Event('input', { bubbles: true });
      input.dispatchEvent(evt);
    }
  }, true);
})();